/** Public rejection facts. Only explicit machine codes may offer a purchase. */
export interface EntitlementFailure {
  code: string;
  action?: string;
  details: Record<string, unknown>;
  retryable?: boolean;
}

const MEMBERSHIP_CODES = new Set([
  'FEATURE_SUBSCRIPTION_REQUIRED', 'WEB_SUBSCRIPTION_REQUIRED', 'PAID_SUBSCRIPTION_REQUIRED',
  'SOUL_ID_SUBSCRIPTION_REQUIRED', 'DIRECTOR_BOARD_SUBSCRIPTION_REQUIRED',
  'MEMBERSHIP_USE_EXHAUSTED', 'PROJECT_LIMIT_EXCEEDED', 'PROJECT_ITEM_LIMIT_EXCEEDED',
]);
const RECOVERY_CODES = new Set(['SOUL_ID_PRICE_CHANGED', 'SOUL_ID_UNAVAILABLE', 'CREDIT_CONFIG_INVALID', 'TOPUP_PACK_LIMIT_REACHED', 'SUBSCRIPTION_EXPIRED', 'APPLE_SUBSCRIPTION_MANAGED_EXTERNALLY', 'AGREEMENT_RE_SIGN_REQUIRED', 'AUTO_RENEW_NOT_AVAILABLE', 'AIRWALLEX_SUBSCRIPTION_ID_MISSING', 'AIRWALLEX_CHECKOUT_UNAVAILABLE', 'AIRWALLEX_CHECKOUT_RECOVERY_UNAVAILABLE', 'AIRWALLEX_CHECKOUT_IN_PROGRESS', 'APPLE_IAP_STOREFRONT_PRICE_NOT_CONFIGURED', 'APPLE_IAP_OFFER_NOT_CONFIGURED']);
const ALIASES: Record<string, string> = {
  UGC_CANVAS_INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  MODEL_NOT_AVAILABLE: 'FEATURE_SUBSCRIPTION_REQUIRED',
  FEATURE_NOT_AVAILABLE: 'FEATURE_SUBSCRIPTION_REQUIRED',
  SUBSCRIPTION_REQUIRED: 'FEATURE_SUBSCRIPTION_REQUIRED',
};
const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const codeOf = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const code = value.trim().toUpperCase();
  const canonical = ALIASES[code] ?? code;
  return canonical === 'INSUFFICIENT_CREDITS' || canonical === 'WALLET_FROZEN' || MEMBERSHIP_CODES.has(canonical) || RECOVERY_CODES.has(canonical) ? canonical : undefined;
};

/** Compatible with HTTP envelopes, SSE, and persisted task/operation metadata. */
export function readEntitlementFailure(value: unknown): EntitlementFailure | null {
  const visited = new Set<object>();
  function read(input: unknown, depth: number): EntitlementFailure | null {
    if (!record(input) || depth > 8 || visited.has(input)) return null;
    visited.add(input);
    if (['active', 'waiting_tasks', 'waiting_user', 'completed', 'stopped'].includes(String(input.state))) return null;
    // The durable public fact wins over a wrapper's generic failure code.
    const explicit = read(input.entitlement_failure ?? input.entitlementFailure, depth + 1);
    if (explicit) return explicit;
    const nested = ['data', 'error', 'detail', 'details', 'metadata', 'context', 'structured_action', 'creative_operation', 'operation', 'hypit_credit_rejection']
      .map(key => read(input[key], depth + 1)).find(Boolean);
    const code = codeOf(input.code) ?? codeOf(input.error_code) ?? codeOf(input.errorCode)
      ?? codeOf(input.legacy_code) ?? codeOf(input.error) ?? codeOf(input.reason);
    if (!code) return nested ?? null;
    const flattened = { ...(nested?.details ?? {}), ...input,
      ...(record(input.detail) ? input.detail : {}), ...(record(input.details) ? input.details : {}),
      ...(record(input.metadata) && record(input.metadata.hypit_credit_rejection) ? input.metadata.hypit_credit_rejection : {}) };
    // Do not store recursive envelopes in persisted UI state.
    for (const key of ['data', 'error', 'detail', 'details', 'metadata', 'context', 'structured_action', 'creative_operation', 'operation', 'entitlement_failure', 'entitlementFailure']) delete flattened[key];
    const publicKeys = ['required', 'requiredCredits', 'required_credits', 'credits_required', 'available', 'availableCredits', 'available_credits', 'current', 'limit', 'current_count', 'max_projects', 'max_items_per_project', 'feature_key', 'model_key', 'current_plan', 'plan_slug', 'required_plans', 'allowed_plans', 'kind', 'period', 'scope_key', 'project_id', 'reset_at', 'reason', 'unit', 'request_id', 'monthly_gift_limit', 'monthly_gifts_remaining'];
    const details = Object.fromEntries(publicKeys.filter(key => flattened[key] !== undefined).map(key => [key, flattened[key]]));
    return { code, details, action: typeof flattened.action === 'string' ? flattened.action : nested?.action,
      retryable: typeof flattened.retryable === 'boolean' ? flattened.retryable : nested?.retryable };
  }
  return read(value, 0);
}

export function entitlementPurchaseIntent(failure: EntitlementFailure | null): 'topup' | 'upgrade' | null {
  if (!failure || failure.code === 'WALLET_FROZEN') return null;
  if (MEMBERSHIP_CODES.has(failure.code) && Array.isArray(failure.details.required_plans) && failure.details.required_plans.length === 0) return null;
  if (failure.action && !['top_up', 'add_credits', 'upgrade', 'subscribe', 'resubscribe', 'renew', 'check_access'].includes(failure.action)) return null;
  return failure.code === 'INSUFFICIENT_CREDITS' ? 'topup' : MEMBERSHIP_CODES.has(failure.code) ? 'upgrade' : null;
}

export function entitlementRequiredCredits(failure: EntitlementFailure | null): number | undefined {
  const detail = failure?.details;
  const amount = detail?.required ?? detail?.requiredCredits ?? detail?.required_credits ?? detail?.credits_required;
  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0 ? amount : undefined;
}

export function isMembershipFailure(failure: EntitlementFailure | null): boolean {
  return Boolean(failure && MEMBERSHIP_CODES.has(failure.code));
}

export function isProjectQuotaError(error: unknown): boolean {
  const code = readEntitlementFailure(error)?.code;
  return code === 'PROJECT_LIMIT_EXCEEDED' || code === 'PROJECT_ITEM_LIMIT_EXCEEDED';
}

export function entitlementMessageKey(failure: EntitlementFailure): string {
  return failure.code === 'INSUFFICIENT_CREDITS' ? 'insufficient'
    : failure.code === 'WALLET_FROZEN' ? 'walletFrozen'
    : failure.action === 'wait_for_reset' ? 'waitForReset'
    : failure.action === 'manage_projects' ? 'manageProjects'
    : failure.action === 'contact_owner' ? 'contactOwner'
    : failure.action === 'refresh_quote' || failure.code === 'SOUL_ID_PRICE_CHANGED' ? 'refreshQuote'
    : failure.code === 'PROJECT_LIMIT_EXCEEDED' ? 'projectLimitReached'
    : failure.code === 'PROJECT_ITEM_LIMIT_EXCEEDED' ? 'projectItemLimitReached'
    : entitlementPurchaseIntent(failure) === 'upgrade' ? 'membershipRequired' : 'supportRequired';
}

export function entitlementAvailableCredits(failure: EntitlementFailure | null): number | undefined {
  const detail = failure?.details;
  const amount = detail?.available ?? detail?.availableCredits ?? detail?.available_credits;
  return typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 ? amount : undefined;
}
